import { OutgoingRequest } from '@wui/jssip/lib/SIPMessage'
import { Subscriber } from '@wui/jssip/lib/Subscriber'
import { defineStore } from 'pinia'
import { ref } from 'vue'

import { useStoreContacts } from '@/entities/contact'
import { notifyBodyParser, useWebRTC } from '@/entities/webrtc'

import { NotifyEventData } from '@/shared/components/notifications'
import { useCallsNotifications } from '@/shared/composables/calls-notifications'
import { useCurrentUser } from '@/shared/composables/current-user'
import { useLocalization } from '@/shared/composables/localization'
import { useUserFeatures } from '@/shared/composables/user-features'
import { FeaturesNames } from '@/shared/types/settings'
import { getAppURL } from '@/shared/utils/electron'
import { useFetchApi } from '@/shared/utils/fetch'

export interface PickupGroup {
  guid: string
  type: string
  name: string
  aclEnabled: boolean
  subscribers: Array<{number: string}>
}
export enum CallStatusState {
  UNKNOWN = 'unknown',
  TERMINATED = 'terminated',
  EARLY = 'early',
  CONFIRMED = 'confirmed',
  HOLD = 'hold',
}
export interface SubscriberStatus {
  internalNumber: string
  sessionId: string
  registered?: boolean
  remote?: CallStatusState
  local?: CallStatusState
  callId?: string | null
  callFromNumber?: string
  callFromName?: string
}

export interface SubscriberStatusWithTime extends SubscriberStatus {
  timestamp: number
}

export const useStorePickupCall = defineStore('pickupCalls', () => {
  const pickupGroups = ref<Array<PickupGroup>>([])
  const _pickupStatuses = ref(new Map<string, SubscriberStatus>())
  const activePickupStatuses = ref(new Map<string, SubscriberStatusWithTime>())
  // Активные SIP-подписки на номера участников групп перехвата (WUI-5581: защита от дублирования при периодическом обновлении групп)
  const _subscribers = new Map<string, Subscriber>()

  const fetchAndSubscribePickupCalls = (): Promise<PickupGroup[]> => {
    return new Promise((resolve) => {
      const fetch = useFetchApi()
      fetch.get<PickupGroup[]>(getAppURL({ pathName: '/api/call-interception/' })).then((response) => {
        pickupGroups.value = response.data
        const uniqueNumbers = new Set(pickupGroups.value.flatMap((group) => {
          return group.subscribers.map((subscriber) => {
            return subscriber.number
          })
        }))
        _subscribe(uniqueNumbers)
        resolve(response.data)
      }).catch((e) => {
        console.error(e)
        resolve([])
      })
    })
  }

  const _subscribe = (numbers: Set<string>) => {
    const { enabledFeatures } = useUserFeatures()
    if (enabledFeatures.value.has(FeaturesNames.SUBSCRIBER_PICKUP)) { // Если группа доступа отключена, не подписываемся на пользователей
      const { subscribe } = useWebRTC()
      const { currentUser } = useCurrentUser()
      const myNumber = currentUser.value?.internalNumber

      // Отписываемся от номеров, которых больше нет в группах перехвата
      _subscribers.forEach((subscriber, number) => {
        if (!numbers.has(number)) {
          subscriber.terminate()
          _subscribers.delete(number)
          _pickupStatuses.value.delete(number)
          const tracked = activePickupStatuses.value.get(number)
          if (tracked) {
            activePickupStatuses.value.delete(number)
            if (tracked.callId) {
              useCallsNotifications().remove(tracked.callId, 'pickup')
            }
          }
        }
      })

      numbers.forEach((number) => {
        if (myNumber && number === myNumber) {
          return
        }
        if (_subscribers.has(number)) { // Уже подписаны — не создаём дубликат SIP-подписки
          return
        }
        try {
          const subscriber = subscribe(number)
          subscriber.on('notify', (isFinal: boolean, request: OutgoingRequest, body: string) => {
            _onNotifyHandler(request, body)
          })
          // Подписка завершилась (например, обрыв транспорта) — переподпишемся при следующем обновлении групп
          subscriber.on('terminated', () => {
            if (_subscribers.get(number) === subscriber) {
              _subscribers.delete(number)
            }
          })
          _subscribers.set(number, subscriber)
        } catch (e) {
          // SIP UA ещё не инициализирован — подписка выполнится при следующем обновлении групп или подключении SIP
          console.error(e)
        }
      })
    }
  }

  const _onNotifyHandler = (notify: OutgoingRequest, body: string) => {
    const { currentUser } = useCurrentUser()
    const { findByInternalNumber } = useStoreContacts()
    const notifyBody = notifyBodyParser(body)
    const sessionId = notify.call_id as string
    const status: SubscriberStatus = { ...notifyBody, sessionId }
    const myNumber = currentUser.value?.internalNumber
    // Свою линию обрабатывает other-terminal store (self-pickup)
    if (myNumber && status.internalNumber === myNumber) {
      return
    }
    const notIAmCaller = myNumber !== notifyBody.callFromNumber

    const ringingStatus = notifyBody.remote && notifyBody.remote === CallStatusState.EARLY

    const callId = status.callId
    if (notIAmCaller && ringingStatus && callId) {
      const { t } = useLocalization()
      let name = t('common.externalContact')
      let fromNumber = ''
      if (status.callFromNumber) {
        const contact = findByInternalNumber(status.callFromNumber)
        if (contact) {
          fromNumber = contact.internalNumber
          name = contact.name
        } else if (status.callFromName) {
          name = status.callFromName
          fromNumber = status.callFromNumber
        } else if (status.callFromNumber) {
          name = status.callFromNumber
          fromNumber = status.callFromNumber
        }
      }
      const getContact = findByInternalNumber(status.internalNumber)
      const targetNumber = getContact || { internalNumber: status.internalNumber, name: status.internalNumber }

      const isInitiator = notifyBody.direction === 'initiator'
      const callerNumber = isInitiator ? status.internalNumber : (fromNumber ?? status.callFromNumber ?? '')
      const callerName = isInitiator ? targetNumber.name : name
      const calleeNumber = isInitiator ? (fromNumber ?? status.callFromNumber ?? '') : targetNumber.internalNumber
      const calleeName = isInitiator ? name : targetNumber.name

      const notifyEvent: NotifyEventData = {
        from: callerNumber,
        fromName: callerName,
        target: calleeNumber,
        targetName: calleeName,
        callId,
        interceptNumber: status.internalNumber,
      }
      useCallsNotifications().add(notifyEvent)
    }

    _pickupStatuses.value.set(status.internalNumber, status)
    if (notIAmCaller && status.remote === CallStatusState.EARLY) {
      activePickupStatuses.value.set(status.internalNumber, {
        ...status,
        timestamp: Date.now(),
      })
    } else if (status.remote !== CallStatusState.EARLY && activePickupStatuses.value.has(status.internalNumber)) {
      const tracked = activePickupStatuses.value.get(status.internalNumber)
      const isOtherDialog = Boolean(callId && tracked?.callId && callId !== tracked.callId)
      if (!isOtherDialog) {
        activePickupStatuses.value.delete(status.internalNumber)
        if (tracked?.callId) {
          useCallsNotifications().remove(tracked.callId, 'pickup')
        }
      }
    }
  }

  return {
    pickupGroups,
    activePickupStatuses,
    fetchAndSubscribePickupCalls,
  }
})
